import { motion } from "framer-motion";
import { Flame, Sparkles, Zap, Snowflake } from "lucide-react";

const MILESTONE_MSG = {
  3: "3 days strong — a habit is forming! 🌱",
  7: "A full week! You're officially on a roll. 🎉",
  14: "Two weeks of consistency. Incredible. 💪",
  21: "21 days — this is who you are now. ✨",
  30: "A whole month! You're unstoppable. 🏆",
  50: "50 days. Legendary consistency. 🔥",
  100: "100 days!! Absolute icon. 👑",
  365: "One year. You've transformed. 🌟",
};

export function StreakNudge({ streak, onScan }) {
  if (!streak) return null;
  const { streak: days, logged_today, at_risk, milestone, freezes = 0, used_freeze } = streak;

  // Config per state
  let cfg;
  if (days === 0) {
    cfg = {
      bg: "bg-card border border-border",
      ring: "bg-secondary text-muted-foreground",
      title: "Start a streak today",
      sub: "Log any meal to light the first flame.",
      cta: "Scan a meal",
      icon: Zap,
      tone: "muted",
    };
  } else if (at_risk) {
    cfg = {
      bg: "bg-accent text-accent-foreground",
      ring: "bg-white/20 text-white",
      title: `Your ${days}-day streak is at risk`,
      sub: "Log a meal before midnight to keep the chain alive.",
      cta: "Keep it alive",
      icon: Flame,
      tone: "accent",
    };
  } else if (milestone) {
    cfg = {
      bg: "bg-primary text-primary-foreground",
      ring: "bg-white/20 text-white",
      title: `${days}-day streak! 🔥`,
      sub: MILESTONE_MSG[days] || "Milestone unlocked. Keep going!",
      icon: Sparkles,
      tone: "primary",
    };
  } else {
    cfg = {
      bg: "bg-primary/8 border border-primary/15",
      ring: "bg-primary/12 text-primary",
      title: `${days}-day streak 🔥`,
      sub: "Logged today — you're on a roll. Keep it up!",
      icon: Flame,
      tone: "soft",
    };
  }

  const Icon = cfg.icon;
  const light = cfg.tone === "accent" || cfg.tone === "primary";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl p-4 flex items-center gap-4 ${cfg.bg}`}
      data-testid="streak-nudge"
    >
      <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${cfg.ring}`}>
        <motion.div
          animate={at_risk || cfg.tone === "primary" ? { scale: [1, 1.12, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <Icon className="w-6 h-6" strokeWidth={2.2} />
        </motion.div>
        {days > 0 && (
          <span
            className={`absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full text-[11px] font-bold flex items-center justify-center ${
              light ? "bg-white text-foreground" : "bg-primary text-primary-foreground"
            }`}
            data-testid="streak-count"
          >
            {days}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm flex items-center gap-2 ${light ? "" : "text-foreground"}`} data-testid="streak-title">
          {cfg.title}
          {freezes > 0 && (
            <span
              className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${light ? "bg-white/20" : "bg-[#3B9BD6]/12 text-[#3B9BD6]"}`}
              data-testid="freeze-badge"
              title="Streak freezes protect a missed day"
            >
              <Snowflake className="w-3 h-3" /> {freezes}
            </span>
          )}
        </div>
        <div className={`text-xs mt-0.5 ${light ? "opacity-90" : "text-muted-foreground"}`}>
          {used_freeze ? "A freeze saved your streak yesterday! ❄️ " : ""}{cfg.sub}
        </div>
      </div>

      {(at_risk || days === 0) && (
        <button
          onClick={onScan}
          data-testid="streak-cta"
          className={`shrink-0 text-xs font-bold px-4 py-2.5 rounded-full transition-transform active:scale-95 ${
            at_risk ? "bg-white text-accent" : "bg-primary text-primary-foreground"
          }`}
        >
          {cfg.cta}
        </button>
      )}
    </motion.div>
  );
}
